(() => {
  'use strict';

  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => Array.from(context.querySelectorAll(selector));
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const formatMoney = (cents, format = window.RD?.moneyFormat || '${{amount}}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents || 0);
    const withDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (Number.isNaN(number)) return '0';
      const parts = (number / 100).toFixed(precision).split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? parts.join(decimal) : parts[0];
    };
    const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!placeholder) return withDelimiters(value);
    let rendered;
    switch (placeholder[1]) {
      case 'amount_no_decimals': rendered = withDelimiters(value, 0); break;
      case 'amount_with_comma_separator': rendered = withDelimiters(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': rendered = withDelimiters(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': rendered = withDelimiters(value, 2, "'", '.'); break;
      default: rendered = withDelimiters(value, 2);
    }
    return format.replace(placeholder[0], rendered);
  };

  const toast = (message) => {
    const element = qs('[data-toast]');
    if (!element) return;
    element.textContent = message;
    element.classList.add('is-visible');
    clearTimeout(window.__rdToast);
    window.__rdToast = setTimeout(() => element.classList.remove('is-visible'), 2600);
  };

  const setBodyLock = (locked) => {
    document.documentElement.classList.toggle('drawer-open', locked);
  };

  const openOverlay = (target, trigger) => {
    if (!target) return;
    target.classList.add('is-open');
    target.setAttribute('aria-hidden', 'false');
    trigger?.setAttribute('aria-expanded', 'true');
    setBodyLock(true);
    const focusable = qs(focusableSelector, target);
    requestAnimationFrame(() => focusable?.focus());
  };

  const closeOverlay = (target) => {
    if (!target) return;
    target.classList.remove('is-open');
    target.setAttribute('aria-hidden', 'true');
    qsa(`[aria-controls="${target.id}"]`).forEach((button) => button.setAttribute('aria-expanded', 'false'));
    setBodyLock(false);
  };

  qsa('[data-drawer-open]').forEach((button) => {
    button.addEventListener('click', () => openOverlay(qs(button.dataset.drawerOpen), button));
  });

  document.addEventListener('click', (event) => {
    const close = event.target.closest('[data-drawer-close]');
    if (close) closeOverlay(close.closest('.overlay-drawer'));

    const overlay = event.target.closest('.overlay-drawer');
    if (overlay && event.target === overlay) closeOverlay(overlay);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    qsa('.overlay-drawer.is-open').forEach(closeOverlay);
    closeCartDrawer();
  });

  const header = qs('[data-site-header]');
  if (header) {
    const updateHeader = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
    updateHeader();
    window.addEventListener('scroll', updateHeader, { passive: true });
  }

  const announcements = qsa('[data-announcement-item]');
  if (announcements.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    let index = 0;
    setInterval(() => {
      announcements[index].classList.remove('is-active');
      index = (index + 1) % announcements.length;
      announcements[index].classList.add('is-active');
    }, 4500);
  }

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.input = qs('input[type="search"]', this);
      this.results = qs('[data-predictive-search]', this);
      this.abortController = null;
      this.timer = null;
      this.input?.addEventListener('input', () => this.onInput());
      this.input?.addEventListener('focus', () => {
        if (this.results?.innerHTML.trim()) this.setOpen(true);
      });
    }

    onInput() {
      clearTimeout(this.timer);
      const term = this.input.value.trim();
      if (term.length < 2) {
        this.results.innerHTML = '';
        this.setOpen(false);
        return;
      }
      this.timer = setTimeout(() => this.fetchResults(term), 180);
    }

    async fetchResults(term) {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.results.classList.add('is-loading');
      try {
        const url = `${window.RD.routes.predictive}?q=${encodeURIComponent(term)}&section_id=predictive-search&resources[type]=product,query&resources[limit]=6&resources[options][unavailable_products]=last`;
        const response = await fetch(url, { signal: this.abortController.signal });
        if (!response.ok) throw new Error('Predictive search failed');
        const text = await response.text();
        const documentFragment = new DOMParser().parseFromString(text, 'text/html');
        const section = documentFragment.querySelector('#shopify-section-predictive-search');
        this.results.innerHTML = section ? section.innerHTML : text;
        this.setOpen(Boolean(this.results.textContent.trim()));
      } catch (error) {
        if (error.name !== 'AbortError') this.results.innerHTML = '';
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    setOpen(open) {
      this.input.setAttribute('aria-expanded', String(open));
      this.results.classList.toggle('is-open', open);
    }
  }

  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

  const cartShell = () => qs('[data-cart-drawer]');

  const openCartDrawer = () => {
    const shell = cartShell();
    if (!shell) return;
    shell.classList.add('is-open');
    shell.setAttribute('aria-hidden', 'false');
    qsa('[data-cart-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
    setBodyLock(true);
    requestAnimationFrame(() => qs('[data-cart-close]', shell)?.focus());
  };

  const closeCartDrawer = () => {
    const shell = cartShell();
    if (!shell) return;
    shell.classList.remove('is-open');
    shell.setAttribute('aria-hidden', 'true');
    qsa('[data-cart-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    setBodyLock(false);
  };

  const updateCartCount = (count) => qsa('[data-cart-count]').forEach((element) => {
    element.textContent = count;
  });

  const replaceCartDrawer = (html) => {
    if (!html) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const replacement = parsed.querySelector('#shopify-section-cart-drawer');
    const current = qs('#shopify-section-cart-drawer');
    if (replacement && current) current.replaceWith(replacement);
  };

  const refreshCartDrawer = async () => {
    const response = await fetch(`${window.RD.routes.cart}?section_id=cart-drawer`);
    if (!response.ok) throw new Error('Unable to refresh cart');
    replaceCartDrawer(await response.text());
  };

  document.addEventListener('click', async (event) => {
    if (event.target.closest('[data-cart-open]')) {
      event.preventDefault();
      openCartDrawer();
    }
    if (event.target.closest('[data-cart-close]')) closeCartDrawer();

    const quantityButton = event.target.closest('[data-cart-quantity]');
    if (quantityButton) {
      quantityButton.disabled = true;
      try {
        const response = await fetch(`${window.RD.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line: Number(quantityButton.dataset.line),
            quantity: Number(quantityButton.dataset.quantity),
            sections: ['cart-drawer'],
            sections_url: window.location.pathname
          })
        });
        if (!response.ok) throw new Error('Cart update failed');
        const cart = await response.json();
        updateCartCount(cart.item_count);
        replaceCartDrawer(cart.sections?.['cart-drawer']);
        openCartDrawer();
      } catch (error) {
        window.location.href = window.RD.routes.cart;
      }
    }
  });

  const submitAddToCart = async (form) => {
    const button = qs('button[type="submit"]', form);
    const original = button?.innerHTML;
    if (button) {
      button.disabled = true;
      button.classList.add('is-loading');
      button.innerHTML = '<span>Adding…</span>';
    }

    const formData = new FormData(form);
    formData.append('sections', 'cart-drawer');
    formData.append('sections_url', window.location.pathname);

    try {
      const response = await fetch(`${window.RD.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
        body: formData
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Unable to add product');

      replaceCartDrawer(result.sections?.['cart-drawer']);
      const cart = await fetch(`${window.RD.routes.cart}.js`).then((item) => item.json());
      updateCartCount(cart.item_count);
      openCartDrawer();
      toast('Added to your bag.');
    } catch (error) {
      toast(error.message || 'Please choose an available option.');
    } finally {
      if (button) {
        button.disabled = false;
        button.classList.remove('is-loading');
        button.innerHTML = original;
      }
    }
  };

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-quick-add], [data-product-form]');
    if (!form) return;
    if (event.submitter?.name === 'checkout') return;
    event.preventDefault();
    submitAddToCart(form);
  });

  qsa('[data-product-root]').forEach((root) => {
    const variantData = qs('[data-variants-json]', root);
    if (!variantData) return;

    const variants = JSON.parse(variantData.textContent);
    const idInput = qs('[name="id"]', root);
    const price = qs('[data-product-price]', root);
    const compare = qs('[data-product-compare]', root);
    const submit = qs('[data-product-submit]', root);
    const submitPrice = qs('[data-submit-price]', root);
    const stickySubmit = qs('[data-sticky-submit]');
    const stickyPrice = qs('[data-sticky-price]');

    const selectedOptions = () => qsa('[data-option-position]', root).map((group) => qs('[data-option-value].is-selected', group)?.dataset.value);

    const updateVariant = () => {
      const selected = selectedOptions();
      const variant = variants.find((item) => item.options.every((option, index) => option === selected[index]));
      if (!variant) return;

      idInput.value = variant.id;
      history.replaceState({}, '', `${window.location.pathname}?variant=${variant.id}`);

      const formattedPrice = formatMoney(variant.price);
      if (price) price.textContent = formattedPrice;
      if (submitPrice) submitPrice.textContent = formattedPrice;
      if (stickyPrice) stickyPrice.textContent = formattedPrice;

      if (compare) {
        compare.textContent = variant.compare_at_price && variant.compare_at_price > variant.price
          ? formatMoney(variant.compare_at_price)
          : '';
      }

      [submit, stickySubmit].filter(Boolean).forEach((button) => {
        button.disabled = !variant.available;
        const label = button.matches('[data-product-submit]') ? qs('span:first-child', button) : button;
        if (label) label.textContent = variant.available ? 'Add to bag' : 'Sold out';
      });
    };

    qsa('[data-option-value]', root).forEach((button) => {
      button.addEventListener('click', () => {
        const group = button.closest('[data-option-position]');
        qsa('[data-option-value]', group).forEach((item) => {
          item.classList.remove('is-selected');
          item.setAttribute('aria-pressed', 'false');
        });
        button.classList.add('is-selected');
        button.setAttribute('aria-pressed', 'true');
        const label = qs('[data-option-label]', group);
        if (label) label.textContent = button.dataset.value;
        updateVariant();
      });
    });

    qs('[data-qty-minus]', root)?.addEventListener('click', () => {
      const input = qs('[name="quantity"]', root);
      input.value = Math.max(1, Number(input.value) - 1);
    });

    qs('[data-qty-plus]', root)?.addEventListener('click', () => {
      const input = qs('[name="quantity"]', root);
      input.value = Number(input.value) + 1;
    });

    stickySubmit?.addEventListener('click', () => submit?.click());

    qsa('[data-media-thumb]', root).forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => {
        const target = qs(`[data-media-id="${thumbnail.dataset.mediaThumb}"]`, root);
        target?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        qsa('[data-media-thumb]', root).forEach((item) => {
          item.classList.remove('is-active');
          item.setAttribute('aria-current', 'false');
        });
        thumbnail.classList.add('is-active');
        thumbnail.setAttribute('aria-current', 'true');
      });
    });
  });

  const filterToggle = qs('[data-filter-toggle]');
  filterToggle?.addEventListener('click', () => filterToggle.closest('.filters')?.classList.toggle('is-open'));
  qsa('[data-auto-submit]').forEach((element) => element.addEventListener('change', () => element.form?.submit()));
})();


// Homepage featured product variant selection.
document.addEventListener('change', (event) => {
  const select = event.target.closest('.home-product__form select[name="id"]');
  if (!select) return;
  const form = select.closest('form');
  const hidden = form?.querySelector('input[type="hidden"][name="id"]');
  if (hidden) hidden.value = select.value;
});

// Visible play, sound, and volume controls for product videos.
document.querySelectorAll('[data-rd-video-player]').forEach((player) => {
  const video = player.querySelector('video');
  if (!video) return;

  const playButton = player.querySelector('[data-video-play]');
  const muteButton = player.querySelector('[data-video-mute]');
  const volumeControl = player.querySelector('[data-video-volume]');

  video.muted = false;
  video.volume = 1;

  const syncPlay = () => {
    if (!playButton) return;
    const paused = video.paused;
    playButton.textContent = paused ? 'Play' : 'Pause';
    playButton.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
  };

  const syncSound = () => {
    if (!muteButton) return;
    const muted = video.muted || video.volume === 0;
    muteButton.textContent = muted ? 'Sound off' : 'Sound on';
    muteButton.setAttribute('aria-label', muted ? 'Turn video sound on' : 'Mute video');
    if (volumeControl && document.activeElement !== volumeControl) {
      volumeControl.value = muted ? 0 : video.volume;
    }
  };

  playButton?.addEventListener('click', async () => {
    if (video.paused) {
      try { await video.play(); } catch (error) { /* Native controls remain available. */ }
    } else {
      video.pause();
    }
    syncPlay();
  });

  muteButton?.addEventListener('click', () => {
    video.muted = !video.muted;
    if (!video.muted && video.volume === 0) video.volume = 1;
    syncSound();
  });

  volumeControl?.addEventListener('input', () => {
    video.volume = Number(volumeControl.value);
    video.muted = video.volume === 0;
    syncSound();
  });

  video.addEventListener('play', syncPlay);
  video.addEventListener('pause', syncPlay);
  video.addEventListener('volumechange', syncSound);
  syncPlay();
  syncSound();
});


// Complete header dropdown accessibility.
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.rd-nav-link').forEach((button) => {
    const item = button.closest('.rd-nav-item');
    button.addEventListener('click', () => {
      const wasOpen = item.classList.contains('is-open');
      document.querySelectorAll('.rd-nav-item.is-open').forEach((other) => { other.classList.remove('is-open'); other.querySelector('.rd-nav-link')?.setAttribute('aria-expanded','false'); });
      item.classList.toggle('is-open', !wasOpen);
      button.setAttribute('aria-expanded', String(!wasOpen));
    });
  });
  document.addEventListener('click', (event) => {
    if (!event.target.closest('.rd-nav-item')) document.querySelectorAll('.rd-nav-item.is-open').forEach((item) => { item.classList.remove('is-open'); item.querySelector('.rd-nav-link')?.setAttribute('aria-expanded','false'); });
  });
});

/* Sitewide Back link: returns to the prior page only when it belongs to this store. */
(() => {
  const backLinks = document.querySelectorAll('[data-rd-back]');
  if (!backLinks.length) return;

  backLinks.forEach((link) => {
    link.addEventListener('click', (event) => {
      let previousPageIsThisStore = false;

      try {
        if (document.referrer) {
          const previousUrl = new URL(document.referrer);
          previousPageIsThisStore = previousUrl.origin === window.location.origin;
        }
      } catch (error) {
        previousPageIsThisStore = false;
      }

      if (previousPageIsThisStore && window.history.length > 1) {
        event.preventDefault();
        window.history.back();
      }
      // Otherwise the link's normal homepage href is used as a safe fallback.
    });
  });
})();

/* Rarest Diamond Favorites — device-local wishlist used by the website and app WebView. */
(() => {
  'use strict';

  const STORAGE_KEY = 'rd:wishlist:v1';
  const qsa = (selector, context = document) => Array.from(context.querySelectorAll(selector));
  const qs = (selector, context = document) => context.querySelector(selector);

  const readWishlist = () => {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value) ? value.filter((handle) => typeof handle === 'string' && handle) : [];
    } catch (error) {
      return [];
    }
  };

  const writeWishlist = (handles) => {
    const unique = Array.from(new Set(handles));
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(unique)); } catch (error) {}
    window.dispatchEvent(new CustomEvent('rd:wishlist-updated', { detail: { handles: unique } }));
    return unique;
  };

  const money = (cents) => {
    const format = window.RD?.moneyFormat || '${{amount}}';
    const value = Number(cents || 0);
    const amount = (value / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return format.replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(value / 100).toLocaleString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace(/,/g, 'X').replace(/\./g, ',').replace(/X/g, '.'));
  };

  const updateWishlistUI = (handles = readWishlist()) => {
    const set = new Set(handles);
    qsa('[data-rd-wishlist-toggle]').forEach((button) => {
      const active = set.has(button.dataset.productHandle || '');
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      const label = qs('[data-rd-wishlist-label]', button);
      if (label) label.textContent = active ? 'Saved to favorites' : 'Save to favorites';
    });
    qsa('[data-rd-wishlist-count]').forEach((count) => { count.textContent = String(handles.length); });
  };

  const toggleHandle = (handle) => {
    if (!handle) return;
    const handles = readWishlist();
    const exists = handles.includes(handle);
    const next = exists ? handles.filter((item) => item !== handle) : [handle, ...handles];
    writeWishlist(next);
    updateWishlistUI(next);

    const toast = qs('[data-toast]');
    if (toast) {
      toast.textContent = exists ? 'Removed from favorites.' : 'Saved to favorites.';
      toast.classList.add('is-visible');
      clearTimeout(window.__rdWishlistToast);
      window.__rdWishlistToast = setTimeout(() => toast.classList.remove('is-visible'), 2200);
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-rd-wishlist-toggle]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    toggleHandle(button.dataset.productHandle);
  });

  const productUrl = (handle) => {
    const root = (window.RD?.routes?.root || '/').replace(/\/?$/, '/');
    return `${root}products/${encodeURIComponent(handle)}`;
  };

  const productJsonUrl = (handle) => `${productUrl(handle)}.js`;

  const normalizeImage = (image) => {
    if (!image) return '';
    if (typeof image === 'object') image = image.src || image.url || '';
    if (typeof image !== 'string') return '';
    if (image.startsWith('//')) return `https:${image}`;
    return image;
  };

  const createFavoriteCard = (product, handle) => {
    const article = document.createElement('article');
    article.className = 'rd-favorite-card';
    article.dataset.favoriteHandle = handle;

    const media = document.createElement('a');
    media.className = 'rd-favorite-card__media';
    media.href = productUrl(handle);
    media.setAttribute('aria-label', product.title || handle);

    const imageUrl = normalizeImage(product.featured_image || product.images?.[0]);
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = product.title || 'Rarest Diamond product';
      image.loading = 'lazy';
      media.appendChild(image);
    }

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'rd-favorite-card__remove';
    remove.dataset.rdWishlistRemove = handle;
    remove.setAttribute('aria-label', `Remove ${product.title || 'item'} from favorites`);
    remove.textContent = '♥';
    const body = document.createElement('div');
    body.className = 'rd-favorite-card__body';

    const eyebrow = document.createElement('p');
    eyebrow.className = 'rd-favorite-card__eyebrow';
    eyebrow.textContent = product.vendor || 'Rarest Diamond® Co.';

    const row = document.createElement('div');
    row.className = 'rd-favorite-card__row';

    const title = document.createElement('h2');
    const titleLink = document.createElement('a');
    titleLink.href = productUrl(handle);
    titleLink.textContent = product.title || handle;
    title.appendChild(titleLink);

    const price = document.createElement('span');
    price.className = 'rd-favorite-card__price';
    price.textContent = money(product.price);

    row.append(title, price);
    body.append(eyebrow, row);
    article.append(media, remove, body);
    return article;
  };

  const renderFavoritesPage = async () => {
    const page = qs('[data-rd-wishlist-page]');
    if (!page) return;

    const grid = qs('[data-rd-wishlist-grid]', page);
    const empty = qs('[data-rd-wishlist-empty]', page);
    const status = qs('[data-rd-wishlist-status]', page);
    const handles = readWishlist();

    grid.innerHTML = '';
    if (!handles.length) {
      empty.hidden = false;
      status.textContent = '0 saved pieces';
      return;
    }

    empty.hidden = true;
    status.textContent = `${handles.length} saved ${handles.length === 1 ? 'piece' : 'pieces'}`;

    const results = await Promise.all(handles.map(async (handle) => {
      try {
        const response = await fetch(productJsonUrl(handle), { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Product unavailable');
        return { handle, product: await response.json() };
      } catch (error) {
        return { handle, product: null };
      }
    }));

    const missing = [];
    results.forEach(({ handle, product }) => {
      if (product) grid.appendChild(createFavoriteCard(product, handle));
      else missing.push(handle);
    });

    if (missing.length) {
      const cleaned = handles.filter((handle) => !missing.includes(handle));
      writeWishlist(cleaned);
      status.textContent = `${cleaned.length} saved ${cleaned.length === 1 ? 'piece' : 'pieces'}`;
      if (!cleaned.length) empty.hidden = false;
    }
  };

  document.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-rd-wishlist-remove]');
    if (!remove) return;
    event.preventDefault();
    event.stopPropagation();
    const handle = remove.dataset.rdWishlistRemove;
    const next = readWishlist().filter((item) => item !== handle);
    writeWishlist(next);
    remove.closest('[data-favorite-handle]')?.remove();
    updateWishlistUI(next);
    const page = qs('[data-rd-wishlist-page]');
    if (page) {
      const status = qs('[data-rd-wishlist-status]', page);
      const empty = qs('[data-rd-wishlist-empty]', page);
      status.textContent = `${next.length} saved ${next.length === 1 ? 'piece' : 'pieces'}`;
      if (!next.length) empty.hidden = false;
    }
  });

  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) updateWishlistUI();
  });
  window.addEventListener('rd:wishlist-updated', (event) => updateWishlistUI(event.detail?.handles || readWishlist()));

  document.addEventListener('DOMContentLoaded', () => {
    updateWishlistUI();
    renderFavoritesPage();
  });

  if (document.readyState !== 'loading') {
    updateWishlistUI();
    renderFavoritesPage();
  }
})();
